import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ButtonSpinner } from '../components/ui/Loader';
import {
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  type ApiKey,
} from '../store/api/userApi';
import { formatDate } from '../utils/dateFormatter';

export function ApiKeysPage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useGetApiKeysQuery();
  const [createApiKey, { isLoading: isCreating }] = useCreateApiKeyMutation();
  const [deleteApiKey, { isLoading: isDeleting }] = useDeleteApiKeyMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [label, setLabel] = useState('');
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; keyId: string | null }>({
    isOpen: false,
    keyId: null,
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (label.length > 255) {
      toast.error(t('apiKeys.create.labelTooLong'));
      return;
    }

    try {
      const result = await createApiKey({ label: label.trim() || undefined }).unwrap();
      setNewApiKey(result.data.apiKey);
      setLabel('');
      setShowCreateForm(false);
      void refetch();
      toast.success(t('apiKeys.create.success'));
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || t('apiKeys.create.error'));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.keyId) return;

    try {
      await deleteApiKey(deleteDialog.keyId).unwrap();
      setDeleteDialog({ isOpen: false, keyId: null });
      void refetch();
      toast.success(t('apiKeys.delete.success'));
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || t('apiKeys.delete.error'));
    }
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success(t('apiKeys.copied'));
    } catch {
      toast.error(t('apiKeys.copyError'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="text-center animate-pulse">
              <p className="text-neutral-medium text-xl font-body">
                {t('common.loading')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-6 text-center">
              <p className="text-red-500 font-body">
                {t('apiKeys.loadError')}
              </p>
              <button
                onClick={() => void refetch()}
                className="mt-4 px-4 py-2 bg-brand-accent text-brand-primary rounded-lg hover:bg-brand-accent/80 transition-colors"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const keys = data?.data.keys || [];

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('apiKeys.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('apiKeys.subtitle')}
            </p>
          </div>

          {/* New API Key Display (if just created) */}
          {newApiKey && (
            <div className="mb-6 bg-yellow-500/20 border border-yellow-500 rounded-lg p-6 animate-scale-in">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-heading font-semibold text-yellow-500 mb-2">
                    {t('apiKeys.newKey.title')}
                  </h3>
                  <p className="text-sm text-neutral-medium font-body mb-4">
                    {t('apiKeys.newKey.warning')}
                  </p>
                </div>
                <button
                  onClick={() => setNewApiKey(null)}
                  className="text-neutral-medium hover:text-neutral-light transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="bg-neutral-dark rounded-lg p-4 mb-4">
                <code className="text-sm text-neutral-light font-mono break-all">
                  {newApiKey}
                </code>
              </div>
              <button
                onClick={() => void handleCopyKey(newApiKey)}
                className="w-full px-4 py-2 bg-brand-accent text-brand-primary rounded-lg hover:bg-brand-accent/80 transition-colors font-heading font-semibold"
              >
                {t('apiKeys.copy')}
              </button>
            </div>
          )}

          {/* Create API Key Form */}
          {showCreateForm && !newApiKey && (
            <div className="mb-6 card-glass rounded-2xl shadow-2xl p-6 animate-scale-in">
              <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
                {t('apiKeys.create.title')}
              </h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label
                    htmlFor="label"
                    className="block text-sm font-body text-neutral-medium mb-2"
                  >
                    {t('apiKeys.create.label')} ({t('apiKeys.create.optional')})
                  </label>
                  <input
                    id="label"
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    maxLength={255}
                    placeholder={t('apiKeys.create.labelPlaceholder')}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors"
                  />
                  <p className="mt-1 text-xs text-neutral-medium font-body">
                    {label.length}/255
                  </p>
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {isCreating ? (
                      <>
                        <ButtonSpinner />
                        <span>{t('common.loading')}</span>
                      </>
                    ) : (
                      t('apiKeys.create.button')
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setLabel('');
                    }}
                    className="px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* API Keys List */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-brand-accent">
                {t('apiKeys.list.title')}
              </h2>
              {!showCreateForm && !newApiKey && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 transition-colors"
                >
                  {t('apiKeys.create.button')}
                </button>
              )}
            </div>

            {keys.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-neutral-medium text-lg font-body mb-4">
                  {t('apiKeys.list.empty')}
                </p>
                {!showCreateForm && !newApiKey && (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 transition-colors"
                  >
                    {t('apiKeys.create.button')}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {keys.map((key: ApiKey) => (
                  <div
                    key={key.keyId}
                    className="bg-neutral-dark rounded-lg p-4 border border-neutral-dark hover:border-brand-accent/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-heading font-semibold text-neutral-light">
                            {key.label || t('apiKeys.list.noLabel')}
                          </h3>
                          <span className="px-2 py-1 bg-brand-accent/20 text-brand-accent text-xs font-body rounded">
                            {key.keyId.substring(0, 8)}...
                          </span>
                        </div>
                        <p className="text-sm text-neutral-medium font-body">
                          {t('apiKeys.list.createdAt')}: {formatDate(key.createdAt, 'PP h:mm a')}
                        </p>
                      </div>
                      <button
                        onClick={() => setDeleteDialog({ isOpen: true, keyId: key.keyId })}
                        disabled={isDeleting}
                        className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-body"
                      >
                        {t('common.remove')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Card */}
          <div className="mt-6 bg-blue-500/20 border border-blue-500 rounded-lg p-6">
            <h3 className="text-lg font-heading font-semibold text-blue-500 mb-2">
              {t('apiKeys.info.title')}
            </h3>
            <ul className="space-y-2 text-sm text-neutral-medium font-body list-disc list-inside">
              <li>{t('apiKeys.info.point1')}</li>
              <li>{t('apiKeys.info.point2')}</li>
              <li>{t('apiKeys.info.point3')}</li>
              <li>{t('apiKeys.info.point4')}</li>
            </ul>
          </div>

          {/* Back to Profile */}
          <div className="mt-6 text-center">
            <Link
              to="/profile"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-colors"
            >
              {t('apiKeys.backToProfile')}
            </Link>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, keyId: null })}
        onConfirm={handleDelete}
        title={t('apiKeys.delete.title')}
        message={t('apiKeys.delete.message')}
        confirmText={t('apiKeys.delete.confirm')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
        type="danger"
      />
    </div>
  );
}

